import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Generic hook for paginated API calls.
 * - Keeps pagination state isolated per hook instance.
 * - Guards against out-of-order responses.
 * - Supports abort via AbortController.
 */
export default function usePaginatedData({
  enabled,
  fetchPage,
  limit = 10,
  initialPage = 1,
  resetKey,
}) {
  const [page, setPage] = useState(initialPage);
  const [totalPages, setTotalPages] = useState(1);
  const [data, setData] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [loaded, setLoaded] = useState(false);

  const requestIdRef = useRef(0);

  const safeFetchPage = useCallback(
    async ({ pageToFetch, signal }) => {
      // fetchPage must return:
      // { data: [...], total_pages: number }
      return fetchPage({ page: pageToFetch, limit, signal });
    },
    [fetchPage, limit]
  );

  // Reset when context changes (employee/filter)
  useEffect(() => {
    if (!enabled) return;
    setPage(initialPage);
    setTotalPages(1);
    setData([]);
    setError("");
    setLoaded(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, resetKey, initialPage]);

  useEffect(() => {
    if (!enabled) return;

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    const controller = new AbortController();
    const signal = controller.signal;

    setLoading(true);
    setError("");

    (async () => {
      try {
        const res = await safeFetchPage({ pageToFetch: page, signal });
        if (requestIdRef.current !== requestId) return;

        const payload = res || {};
        const nextData = payload.data ?? payload.rows ?? [];
        const nextTotalPages = payload.total_pages ?? payload.totalPages ?? 1;

        setData(Array.isArray(nextData) ? nextData : []);
        setTotalPages(Number(nextTotalPages) || 1);
        setLoaded(true);
      } catch (err) {
        if (requestIdRef.current !== requestId) return;
        if (controller.signal?.aborted) return;

        setData([]);
        setTotalPages(1);
        setLoaded(true);
        setError("Failed to load data. Please try again.");
      } finally {
        // Only the latest request can close its own loading state.
        if (requestIdRef.current === requestId) {
          setLoading(false);
        }
      }
    })();

    return () => {
      controller.abort();
    };
  }, [enabled, page, safeFetchPage]);

  return {
    data,
    loading,
    error,
    loaded,
    page,
    totalPages,
    setPage,
  };
}

